import type { EntryModule, ScenarioEntry, ScenarioProjection } from '../domain/types';
/** Presentation-only editorial relations. IDs refer to fixed ST content; no Owner approval implied. */
export const mappingNature = 'EDITORIAL_RESEARCH_MAPPING' as const;
export const issueLabels: Readonly<Record<string, string>> = {
  'HRF-HZ-01': '火势发展与蔓延',
  'HRF-HZ-02': '烟气竖向蔓延',
  'HRF-HZ-03': '人员受困与疏散',
  'HRF-HZ-04': '楼梯与疏散受阻',
  'HRF-HZ-05': '建筑结构与分隔失效',
  'HRF-HZ-06': '外围坠物',
  'HRF-HZ-07': '进入与部署受阻',
  'HRF-HZ-08': '建筑系统失效',
  'HRF-HZ-09': '高处供水不足',
  'HRF-HZ-10': '特殊用途与危化风险',
  'HRF-HZ-11': '通信衰减',
  'HRF-HZ-12': '机器室内作业风险',
};
export const informationLabels: Readonly<Record<string, string>> = {
  'HRF-IR-01': '事故位置与建筑身份',
  'HRF-IR-02': '建筑竖向结构',
  'HRF-IR-03': '建筑使用性质',
  'HRF-IR-04': '起火位置与蔓延范围',
  'HRF-IR-05': '烟气状态与流动方向',
  'HRF-IR-06': '人员处境与疏散需求',
  'HRF-IR-07': '疏散与进入通道',
  'HRF-IR-08': '建筑消防系统状态',
  'HRF-IR-09': '能源与公用设施状态',
  'HRF-IR-10': '危化品与特殊用途风险',
  'HRF-IR-11': '结构状态线索',
  'HRF-IR-12': '响应能力的声明',
  'HRF-IR-13': '机器可用性与就绪声明',
  'HRF-IR-14': '环境与天气',
  'HRF-IR-15': '通信条件',
  'HRF-IR-16': '现场指挥与权限结构',
};
export const capabilityLabels: Readonly<Record<string, string>> = {
  'HRF-CR-01': '高处进入与撤离',
  'HRF-CR-02': '室内火势抑制',
  'HRF-CR-03': '高处供水与输送',
  'HRF-CR-04': '烟气环境生命搜索',
  'HRF-CR-05': '烟气管理与通风',
  'HRF-CR-06': '疏散支持',
  'HRF-CR-07': '破拆与进入工具',
  'HRF-CR-08': '结构状态评估',
  'HRF-CR-09': '危化识别与评估',
  'HRF-CR-10': '能源系统隔离',
  'HRF-CR-11': '现场与室内照明',
  'HRF-CR-12': '通信与楼内中继',
  'HRF-CR-13': '高处远距与热成像观察',
  'HRF-CR-14': '机器远程感知',
  'HRF-CR-15': '机器空间检查',
  'HRF-CR-16': '机器载荷递送',
  'HRF-CR-17': '机器灭火支持',
  'HRF-CR-18': '证据记录',
  'HRF-CR-19': '外围隔离与交通管理',
};
export const machineLabels: Readonly<Record<string, string>> = {
  'HRF-MF-01': '高危险区域远程侦察',
  'HRF-MF-02': '环境与气体检查',
  'HRF-MF-03': '建筑外围观察',
  'HRF-MF-04': '危化区域保持距离识别',
  'HRF-MF-05': '传感器与载荷递送',
  'HRF-MF-06': '烟控期间持续观察',
  'HRF-MF-07': '持续状态监测',
  'HRF-MF-08': '灭火后不确定空间复查',
};
interface EditorialRelation {
  readonly information: readonly string[];
  readonly capability: readonly string[];
  readonly machine: readonly string[];
  readonly devices: readonly string[];
  readonly reason: string;
  readonly question: string;
}
export const issueRelations: Readonly<Record<string, EditorialRelation>> = {
  'HRF-HZ-02': {
    information: ['HRF-IR-02', 'HRF-IR-05', 'HRF-IR-07', 'HRF-IR-08', 'HRF-IR-04'],
    capability: ['HRF-CR-05', 'HRF-CR-13', 'HRF-CR-15', 'HRF-CR-12'],
    machine: ['HRF-MF-01', 'HRF-MF-02', 'HRF-MF-06'],
    devices: ['m30t', 'elios', 'colossus'],
    reason:
      '研究烟气竖向连通、传播与通道状态，理解观察、检查和通信需求。清单顺序仅是编辑呈现，不是操作优先级。',
    question: '哪些竖向路径受到烟气影响？烟控状态、疏散路线和信息来源是否已由专业人员确认？',
  },
  'HRF-HZ-03': {
    information: ['HRF-IR-06', 'HRF-IR-03', 'HRF-IR-07', 'HRF-IR-05'],
    capability: ['HRF-CR-04', 'HRF-CR-06', 'HRF-CR-12'],
    machine: ['HRF-MF-01', 'HRF-MF-05'],
    devices: ['elios', 'm30t'],
    reason: '研究人员分布、协助疏散需求和可到达性；关注生命搜索、疏散支持与通信条件。',
    question: '人员位置、用途与特殊疏散需求是否有可靠来源？观察线索不能确认生命安全。',
  },
  'HRF-HZ-04': {
    information: ['HRF-IR-07', 'HRF-IR-02', 'HRF-IR-08', 'HRF-IR-06'],
    capability: ['HRF-CR-01', 'HRF-CR-06', 'HRF-CR-15', 'HRF-CR-12'],
    machine: ['HRF-MF-01', 'HRF-MF-06'],
    devices: ['elios'],
    reason: '研究楼梯间、通道和建筑进入条件，保留现场通行与授权条件未知。',
    question: '哪些楼梯间可通行？建筑图纸不能替代现场确认。',
  },
  'HRF-HZ-01': {
    information: ['HRF-IR-04', 'HRF-IR-05', 'HRF-IR-08', 'HRF-IR-02'],
    capability: ['HRF-CR-02', 'HRF-CR-13', 'HRF-CR-17', 'HRF-CR-12'],
    machine: ['HRF-MF-01', 'HRF-MF-03', 'HRF-MF-07'],
    devices: ['m30t', 'colossus', 'rs3'],
    reason: '研究起火范围、防火分隔与消防系统条件；比较远距观察与地面灭火技术研究。',
    question: '起火范围和防火分隔是否已有可靠事实？设备研究不代表高层可达。',
  },
  'HRF-HZ-05': {
    information: ['HRF-IR-11', 'HRF-IR-02', 'HRF-IR-04', 'HRF-IR-08'],
    capability: ['HRF-CR-08', 'HRF-CR-13', 'HRF-CR-12'],
    machine: ['HRF-MF-03', 'HRF-MF-08'],
    devices: ['m30t'],
    reason: '研究结构状态线索、建筑配置和受火范围；影像辅助观察不能代替安全鉴定。',
    question: '结构图纸与专业损伤评估依据是否明确？',
  },
  'HRF-HZ-06': {
    information: ['HRF-IR-01', 'HRF-IR-07', 'HRF-IR-04', 'HRF-IR-11'],
    capability: ['HRF-CR-08', 'HRF-CR-13', 'HRF-CR-19'],
    machine: ['HRF-MF-03', 'HRF-MF-07'],
    devices: ['m30t'],
    reason: '研究危险立面、外围接近及集结区域；隔离与结构判断仍由专业 Owner 确认。',
    question: '受影响立面、隔离区域与场地使用条件是否已核实？',
  },
};
export type StudyModule = 'information' | 'capability' | 'machine';
export interface EditorialEntryStudy {
  readonly why: string;
  readonly expectation: string;
}
const study = (why: string, expectation: string): EditorialEntryStudy => ({ why, expectation });
/** Each explanation is editorial research context, never a formed source amendment or action. */
export const entryStudies: Readonly<
  Record<
    string,
    Readonly<Record<'information' | 'capability', Readonly<Record<string, EditorialEntryStudy>>>>
  >
> = {
  'HRF-HZ-02': {
    information: {
      'HRF-IR-02': study(
        '楼梯间、电梯井与竖井的连通关系决定烟气可能跨越哪些楼层。',
        '楼层与竖向通道配置、分隔位置及图纸来源；实际烟气传播仍待核实。',
      ),
      'HRF-IR-05': study(
        '烟气方向与能见度用于区分当前观察到的传播和仅由图纸推测的路径。',
        '带时间和观察位置的烟气方向、能见度及受影响通道记录。',
      ),
      'HRF-IR-07': study(
        '烟气可能进入疏散或接近路线，需要辨别哪些通道受影响。',
        '各楼梯间和进入点的烟气影响、现场通行记录及其时效。',
      ),
      'HRF-IR-08': study(
        '烟控、供电和探测系统的实际状态影响烟气控制条件，不能由竖井图纸得出。',
        '烟控与相关消防系统的状态、异常、确认责任及确认时间。',
      ),
      'HRF-IR-04': study(
        '起火层和蔓延范围为研究烟气起点及其上方影响提供空间参照。',
        '起火分隔空间、观察到的蔓延范围和各线索的来源时间。',
      ),
    },
    capability: {
      'HRF-CR-05': study(
        '竖向通道烟气传播需要研究烟气管理的条件与影响。',
        '比较烟气管理与通风类别；具体方法须由专业人员结合系统状态判断。',
      ),
      'HRF-CR-13': study(
        '室内通道观察受限时，外围高处观察可补充外部烟气与热分布线索。',
        '研究远距影像补充观察的范围与遮挡限制；不能推断墙后状态。',
      ),
      'HRF-CR-15': study(
        '受烟气影响的楼梯间或竖井可能缺少可近距离核实的空间线索。',
        '研究远程空间检查的感知与环境条件；不据此认定路线安全。',
      ),
      'HRF-CR-12': study(
        '跨楼层烟气观察需要保留位置、时间和通信可达性，楼内遮挡可能中断回传。',
        '研究楼内覆盖与中继条件，支持线索回传；链路可用性未验证。',
      ),
    },
  },
  'HRF-HZ-03': {
    information: {
      'HRF-IR-06': study(
        '受困者位置和协助需求决定人员搜索与疏散研究关注哪些空间和人群。',
        '人员数量估计、所在楼层、行动受限或协助需求及报告来源；不视为生命确认。',
      ),
      'HRF-IR-03': study(
        '住宅、办公或其他用途的占用特点不同，影响可能在场人员与疏散需求。',
        '各楼层用途、占用性质及其依据，保留未知的实时在场人数。',
      ),
      'HRF-IR-07': study(
        '人员所在位置必须与可到达、可离开的路线一起研究，位置线索本身不代表能疏散。',
        '人员位置关联的楼梯间、接近点和通行障碍的现场确认记录。',
      ),
      'HRF-IR-05': study(
        '人员所在空间的烟气和能见度影响观察可靠性与疏散条件。',
        '人员相关区域的烟气状态、能见度与记录时间；可生存条件仍未验证。',
      ),
    },
    capability: {
      'HRF-CR-04': study(
        '受困人员可能处于低能见度空间，需要研究人员定位与搜索能力。',
        '比较生命搜索的感知方式与适用条件；观察线索不能确认生命安全。',
      ),
      'HRF-CR-06': study(
        '行动受限或需要协助的人群仅被定位仍不足以形成疏散支持。',
        '研究人员移动与辅助疏散的能力类别，结合通道和协助需求进一步核实。',
      ),
      'HRF-CR-12': study(
        '人员位置、求助与搜索反馈可能来自不同楼层，失联会造成线索断裂。',
        '研究求助和人员信息的楼内回传条件，保留身份、位置与确认层级。',
      ),
    },
  },
  'HRF-HZ-04': {
    information: {
      'HRF-IR-07': study(
        '通道受阻首先需要区分实际障碍、受影响区段和可达的进入点。',
        '楼梯间、进入点及接近路线的障碍位置、现场通行记录与时效。',
      ),
      'HRF-IR-02': study(
        '楼梯间与楼层的连接配置用于研究障碍影响范围及其他路径的结构条件。',
        '楼层连接、楼梯间配置与分隔位置；不从图纸推断替代路线可通行。',
      ),
      'HRF-IR-08': study(
        '烟控、电梯迫降或供电异常可能改变楼梯与进入路径的条件。',
        '相关消防系统的异常、当前状态与确认时间。',
      ),
      'HRF-IR-06': study(
        '障碍影响需要结合人员所在楼层与协助需求，不能只统计通道数量。',
        '受影响楼层的人员位置估计、协助需求及来源可靠性。',
      ),
    },
    capability: {
      'HRF-CR-01': study(
        '楼梯或进入路径受阻时，高处到达与撤离的条件需要另行研究。',
        '比较高处进入与撤离能力的环境条件；可到达性未验证。',
      ),
      'HRF-CR-06': study(
        '通道障碍可能使需要协助的人群无法按原路径移动。',
        '研究受阻路径下的辅助疏散需求；不据此指定替代路线。',
      ),
      'HRF-CR-15': study(
        '障碍区段可能缺少近距离空间信息，远程检查可研究其感知条件。',
        '研究障碍和空间状态的远程检查方式；影像不等于通行许可。',
      ),
      'HRF-CR-12': study(
        '分散楼层和障碍区段的信息需要持续回传，遮挡可能造成通信盲区。',
        '研究楼梯与楼内通信覆盖，支持障碍位置和通行信息的更新。',
      ),
    },
  },
  'HRF-HZ-01': {
    information: {
      'HRF-IR-04': study(
        '起火分隔空间和观察到的燃烧范围是研究火势蔓延的直接线索。',
        '起火层、燃烧物、分隔空间及蔓延记录，标明观察时间和来源。',
      ),
      'HRF-IR-05': study(
        '烟气变化可补充火势状态观察，但不能单独证明燃烧范围。',
        '与起火区域关联的烟气方向、能见度和变化时间。',
      ),
      'HRF-IR-08': study(
        '喷淋、立管及相关系统的状态影响研究火势抑制所需的条件。',
        '相关系统当前状态、异常与确认依据；不推定灭火有效。',
      ),
      'HRF-IR-02': study(
        '楼层、竖井与分隔配置有助于研究跨层蔓延的可能路径。',
        '起火区相邻空间和竖向连接的配置及图纸依据。',
      ),
    },
    capability: {
      'HRF-CR-02': study(
        '室内火势发展涉及抑制燃烧的能力需求，而非仅观察状态。',
        '研究室内火势抑制类别及环境条件；具体应用与权限待核实。',
      ),
      'HRF-CR-13': study(
        '高处外围观察可补充外部火势与热分布，室内视野仍有遮挡。',
        '研究远距可见光与热成像的观察范围，不推断墙后火势。',
      ),
      'HRF-CR-17': study(
        '水流应用点可能存在人员暴露，需要研究机器灭火支持的条件。',
        '比较远程灭火支持类别，核实进入、供水、承载与控制链路。',
      ),
      'HRF-CR-12': study(
        '火势变化和观察位置需要回传，通信中断会使状态信息失去时效。',
        '研究楼内观察与抑制支持之间的通信覆盖，不视为链路保证。',
      ),
    },
  },
  'HRF-HZ-05': {
    information: {
      'HRF-IR-11': study(
        '异常变形、损伤与分隔完整性线索直接关联结构或分隔失效研究。',
        '损伤位置、观察方法、专业评估线索与时间；安全结论仍未知。',
      ),
      'HRF-IR-02': study(
        '受损线索需要建筑空间与连接配置作为定位和影响范围参照。',
        '相关楼层、竖向通道及分隔配置的图纸依据。',
      ),
      'HRF-IR-04': study(
        '受火位置和范围有助于研究哪些结构或分隔可能受到火灾影响。',
        '受火分隔空间、蔓延与观察时间；不由受火范围推定承载能力。',
      ),
      'HRF-IR-08': study(
        '相关消防系统异常需与建筑损伤线索区分，避免把系统恢复等同结构恢复。',
        '相关系统异常和状态确认记录，保留结构安全独立待评估。',
      ),
    },
    capability: {
      'HRF-CR-08': study(
        '损伤线索需要专业结构与分隔完整性评估，普通影像不能形成安全鉴定。',
        '研究专业评估能力的依据、环境条件与责任要求。',
      ),
      'HRF-CR-13': study(
        '危险高处的外围损伤可能需要保持距离收集观察线索。',
        '研究高处影像观察范围与精度限制，不替代结构评估。',
      ),
      'HRF-CR-12': study(
        '结构异常与位置记录需要可靠回传，信息延迟会影响线索的时效。',
        '研究损伤观察的通信覆盖及中继条件，保留评估责任。',
      ),
    },
  },
  'HRF-HZ-06': {
    information: {
      'HRF-IR-01': study(
        '建筑身份与具体位置是定位受影响外围、接近区和相邻空间的基础。',
        '建筑身份、事故位置与定位依据，不补造隔离范围。',
      ),
      'HRF-IR-07': study(
        '坠物可能影响接近路线和集结空间，需要核实哪些场地受到影响。',
        '外围接近点、路线、集结区与通行条件的现场记录。',
      ),
      'HRF-IR-04': study(
        '受火立面与蔓延范围可为外围坠物风险研究提供空间参照。',
        '受火楼层、外围观察范围和时间；不推定具体坠落物或落点。',
      ),
      'HRF-IR-11': study(
        '外立面或结构异常线索有助于研究坠物来源，不能由影像判定稳定。',
        '异常构件位置、损伤观察与专业评估依据。',
      ),
    },
    capability: {
      'HRF-CR-08': study(
        '外围构件异常需要专业评估其结构与完整性，避免只依赖外观判断。',
        '研究结构评估所需的依据与责任；稳定性未验证。',
      ),
      'HRF-CR-13': study(
        '危险立面不宜仅依靠人员近距离获取线索，需要研究远距观察条件。',
        '研究外围高处影像的覆盖与遮挡限制，不证明立面安全。',
      ),
      'HRF-CR-19': study(
        '坠物可能影响接近与集结区域，需要研究外围人员和交通隔离的需求。',
        '研究外围警戒与交通管理类别；具体范围和实施权限待专业 Owner 确认。',
      ),
    },
  },
};
export function entryStudyRelation(
  issue: string,
  module: EntryModule,
  sourceId: string,
): EditorialEntryStudy | undefined {
  if (module !== 'information' && module !== 'capability') return undefined;
  if (!issueRelations[issue]?.[module].includes(sourceId)) return undefined;
  return entryStudies[issue]?.[module][sourceId];
}
export type LinkedResult =
  | { readonly status: 'AVAILABLE'; readonly entries: readonly ScenarioEntry[] }
  | {
      readonly status: 'UNKNOWN' | 'MISSING_REFERENCE';
      readonly entries: readonly ScenarioEntry[];
      readonly message: string;
    };
export function linkedEntries(
  data: ScenarioProjection,
  issue: string,
  module: StudyModule,
): LinkedResult {
  const relation = issueRelations[issue];
  if (!relation)
    return {
      status: 'UNKNOWN',
      entries: [],
      message: '尚无经确认的逐项关联；本问题的编辑性研究映射尚未提供。',
    };
  const entries: ScenarioEntry[] = [];
  for (const id of relation[module]) {
    if (module !== 'machine' && !entryStudyRelation(issue, module, id))
      return {
        status: 'MISSING_REFERENCE',
        entries: [],
        message: '研究映射的逐项说明缺失：' + issue + ' / ' + id + '。不会使用通用理由替代。',
      };
    const entry = data.entries[module].find((row) => row.code === id);
    if (!entry)
      return {
        status: 'MISSING_REFERENCE',
        entries: [],
        message: '研究映射的源条目不可达：' + id + '。不会回退到全部清单。',
      };
    entries.push(entry);
  }
  return { status: 'AVAILABLE', entries };
}
export function entryLabel(entry: ScenarioEntry, module: EntryModule) {
  return (
    (module === 'risks'
      ? issueLabels
      : module === 'information'
        ? informationLabels
        : module === 'capability'
          ? capabilityLabels
          : module === 'machine'
            ? machineLabels
            : {})[entry.code] ?? entry.name
  );
}
export const publicReferences = [
  {
    id: 'NIST-WIND',
    name: 'NIST 高层风驱火实验',
    url: 'https://www.nist.gov/publications/fire-fighting-tactics-under-wind-driven-fire-conditions-7-story-building-experiments',
    type: '政府科研',
  },
  {
    id: 'NIST-PPV',
    name: 'NIST 高层楼梯间正压通风实验',
    url: 'https://www.nist.gov/publications/evaluating-positive-pressure-ventilation-large-structures-high-rise-pressure',
    type: '政府科研',
  },
  {
    id: 'NIST-CREW',
    name: 'NIST 高层人员与资源实验',
    url: 'https://www.nist.gov/el/landmark-high-rise-fire-study-description-experiments',
    type: '政府科研',
  },
  {
    id: 'NIMS',
    name: 'USFA/FEMA 指挥与协调',
    url: 'https://www.usfa.fema.gov/a-z/nims/command-and-coordination.html',
    type: '境外制度参考',
  },
  {
    id: 'OSHA',
    name: 'OSHA 呼吸防护法规',
    url: 'https://www.osha.gov/laws-regs/regulations/standardnumber/1910/1910.134',
    type: '美国法规，非本地 SOP',
  },
  {
    id: 'DJI',
    name: 'DJI Matrice 30T 官方规格',
    url: 'https://enterprise.dji.com/matrice-30/specs',
    type: '厂商声明',
  },
  {
    id: 'ELIOS',
    name: 'Flyability Elios 3 官方功能',
    url: 'https://www.flyability.com/elios-3',
    type: '厂商声明',
  },
  {
    id: 'ELIOS-LIMIT',
    name: 'Elios 3 返航与障碍限制',
    url: 'https://knowledge.flyability.com/aircraft/elios-3/elios-3-feature-details/elios-3-return-to-home-and-resume-inspection-user-guide',
    type: '厂商限制说明',
  },
  {
    id: 'ELIOS-HEAT',
    name: 'Elios 3 过热警告',
    url: 'https://knowledge.flyability.com/aircraft/elios-3/elios-3-troubleshooting/elios-3-warning-messages',
    type: '厂商限制说明',
  },
  {
    id: 'COLOSSUS',
    name: 'Shark Robotics Colossus',
    url: 'https://www.shark-robotics.com/colossus-firefighting-robot/',
    type: '厂商声明',
  },
  {
    id: 'LAFD',
    name: 'LAFD Thermite RS3 消防部门案例',
    url: 'https://lafd.org/news/lafd-debuts-rs3-first-robotic-firefighting-vehicle-united-states',
    type: '境外消防机构公开案例',
  },
] as const;
export interface DeviceResearch {
  readonly id: string;
  readonly name: string;
  readonly purpose: string;
  readonly limits: string;
  readonly sources: readonly string[];
  readonly asset: 'drone' | 'caged' | 'robot';
}
export const deviceResearch: readonly DeviceResearch[] = [
  {
    id: 'm30t',
    name: 'DJI Matrice 30T',
    purpose: '外部可见光与热成像观察，研究建筑外围异常热源与热分布线索。',
    limits:
      '热成像不测量气体浓度；不能看到墙后人员，也不代表楼内搜救或高温烟气适用。需核实遮挡、飞行、通信与当地许可。',
    sources: ['DJI'],
    asset: 'drone',
  },
  {
    id: 'elios',
    name: 'Flyability Elios 3',
    purpose: '有限空间与无 GNSS 室内检查研究，LiDAR、热成像与空间建模可提供观察线索。',
    limits:
      '工业检查能力不等于活火救援认证。高温、烟尘、风、玻璃、动态障碍、定位与通信均需验证；返航不保证避开所有障碍。',
    sources: ['ELIOS', 'ELIOS-LIMIT', 'ELIOS-HEAT'],
    asset: 'caged',
  },
  {
    id: 'colossus',
    name: 'Shark Robotics Colossus',
    purpose: '遥控地面消防与灭火支持研究，比较降低人员在应用点暴露的技术方向。',
    limits:
      '不能推断可上高层或进入任何火场。进入路径、通道净空、供水、地面承载、运输与遥控链路需逐项验证。',
    sources: ['COLOSSUS'],
    asset: 'robot',
  },
  {
    id: 'rs3',
    name: 'Thermite RS3（LAFD 案例）',
    purpose: '境外消防部门公开使用的遥控地面车辆案例，包含视频反馈与消防水流应用。',
    limits:
      '境外机构使用记录不等于本地采购、归属、环境验证、可用性或高层可达。本平台未接入该资产。',
    sources: ['LAFD'],
    asset: 'robot',
  },
];
export const publicSourceChecked = '2026-10-10';
export const informationDescriptions: Readonly<Record<string, string>> = {
  'HRF-IR-01': '具体事故位置、建筑身份与定位依据。',
  'HRF-IR-02': '楼层、楼梯间、电梯井、裙房、退台与地下空间的配置。',
  'HRF-IR-03': '建筑各楼层的用途和占用性质。',
  'HRF-IR-04': '起火楼层、分隔空间、观察到的蔓延与燃烧物。',
  'HRF-IR-05': '能见度、烟气方向与受影响的竖向通道。',
  'HRF-IR-06': '人员数量估计、楼层分布与协助疏散需求。',
  'HRF-IR-07': '可通行楼梯间、进入点、接近路线、集结与登高条件。',
  'HRF-IR-08': '探测、喷淋、立管、烟控、电梯迫降与供电状态。',
  'HRF-IR-09': '燃气、电力、光伏、储能与车辆等能源状态。',
  'HRF-IR-10': '危化品以及建筑特殊用途相关风险。',
  'HRF-IR-11': '防火分隔完整性、结构受损与异常状态线索。',
  'HRF-IR-12': '响应能力类别与声明的操作环境。',
  'HRF-IR-13': '机器声明的可用性与就绪类别；不表示物理可用。',
  'HRF-IR-14': '天气、风、环境温度、时间与外部能见度。',
  'HRF-IR-15': '通信覆盖、楼内盲区与信道状态。',
  'HRF-IR-16': '现场各类决策所需的指挥与权限责任。',
};
export const capabilityDescriptions: Readonly<Record<string, string>> = {
  'HRF-CR-01': '研究到达高处、撤离高处与支持人员同类移动的能力。',
  'HRF-CR-02': '研究建筑内部降低或抑制火势的能力类别。',
  'HRF-CR-03': '研究建立并维持高处供水的能力。',
  'HRF-CR-04': '研究低能见度环境下定位与救助人员的能力。',
  'HRF-CR-05': '研究改变竖向通道与分隔空间内烟气流动的能力。',
  'HRF-CR-06': '研究人员移出建筑及辅助疏散的支持能力。',
  'HRF-CR-07': '研究封闭路线的进入与破拆工具。',
  'HRF-CR-08': '研究结构与防火分隔完整性的专业评估能力。',
  'HRF-CR-09': '研究危化品识别与评估能力。',
  'HRF-CR-10': '研究能源与公用设施系统隔离能力。',
  'HRF-CR-11': '研究现场和建筑内部照明能力。',
  'HRF-CR-12': '研究高层遮挡条件下的通信中继与楼内覆盖。',
  'HRF-CR-13': '研究高处与远距观察，含热成像和低能见度感知。',
  'HRF-CR-14': '研究无需人员在观察点的机器远程感知。',
  'HRF-CR-15': '研究机器对结构或空间的远程检查。',
  'HRF-CR-16': '研究机器将小型物品或传感器送至某位置。',
  'HRF-CR-17': '研究人员不在水流应用点的机器灭火支持。',
  'HRF-CR-18': '研究观察内容与时间的证据记录。',
  'HRF-CR-19': '研究外围隔离、警戒与交通管理。',
};
export const machineDescriptions: Readonly<Record<string, string>> = {
  'HRF-MF-01': '区域：起火层以上室内。可生存条件未知时研究远程侦察，减少信息最缺乏时的人员暴露。',
  'HRF-MF-02': '区域：充满烟气的分隔空间。研究进入判断所需的环境检测；热成像不能代替气体测量。',
  'HRF-MF-03': '区域：建筑高处外立面。研究从建筑外观察外围状态，避免人员置于未经评估的立面。',
  'HRF-MF-04': '区域：可能存在危化品的位置。研究保持距离识别未知危险，而不进入该区域。',
  'HRF-MF-05': '区域：人员难以到达的分隔空间。研究小型传感器或通信载荷递送，以建立联系或感知。',
  'HRF-MF-06': '区域：楼梯间或竖井。研究烟气管理期间在竖向通道内的持续感知。',
  'HRF-MF-07': '区域：外围与集结区。研究持续状态监测，减少疲劳与注意力变化的影响。',
  'HRF-MF-08': '区域：灭火后的分隔空间。研究结构仍不确定时的远程复查；不适用于推断火灾中人员搜索。',
};
export function entryDescription(entry: ScenarioEntry, module: EntryModule): string {
  return (
    (module === 'information'
      ? informationDescriptions
      : module === 'capability'
        ? capabilityDescriptions
        : module === 'machine'
          ? machineDescriptions
          : {})[entry.code] ?? '完整源含义见详情依据；未补入缺失事实。'
  );
}
